import * as React from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";

import { useDashboards } from "../hooks/query/useDashboards";
import type { Dashboard } from "../types";
import DialogContext from "../contexts/DialogContext";
import { DeleteButton } from "../components/buttons/DeleteButton";
import RowActionsMenu from "../components/RowActionsMenu";
import { COLORS } from "../theme/theme-options";
import { HealthScore } from "../components/services/HealthScore";
import { useServiceMap } from "../hooks/query/useServiceMap";
import { ServiceMapDiagram } from "../components/ServiceMapDiagram";

const DASHBOARD_PREVIEW_HEIGHT = 208;
const DASHBOARD_SKELETON_COUNT = 2;

const DashboardDiagramPreview: React.FC<{ serviceMapId: string }> = ({
  serviceMapId,
}) => {
  const { data: serviceMap, isLoading } = useServiceMap(serviceMapId);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: DASHBOARD_PREVIEW_HEIGHT,
          borderRadius: 1.5,
          background: `linear-gradient(135deg, ${alpha(COLORS.grey[700], 0.25)}, ${alpha(
            COLORS.grey[900],
            0.35,
          )})`,
        }}
      />
    );
  }

  if (!serviceMap || serviceMap.nodes.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: DASHBOARD_PREVIEW_HEIGHT,
          borderRadius: 1.5,
          display: "grid",
          placeItems: "center",
          color: COLORS.grey[500],
          background: `linear-gradient(180deg, ${alpha(COLORS.grey[800], 0.45)}, ${alpha(
            COLORS.grey[900],
            0.45,
          )})`,
        }}
      >
        <Typography sx={{ fontSize: 13 }}>No services yet</Typography>
      </Box>
    );
  }

  return (
    <ServiceMapDiagram
      nodes={serviceMap.nodes}
      edges={serviceMap.edges}
      variant="full"
      interactive={false}
      preservePositions
      height={DASHBOARD_PREVIEW_HEIGHT}
    />
  );
};

const DashboardCardSkeleton: React.FC = () => {
  return (
    <Card
      variant="glass"
      sx={{
        p: 1.6,
        borderRadius: 2.2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        border: `1px solid ${alpha(COLORS.grey[700], 0.75)}`,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <DashboardRoundedIcon sx={{ fontSize: 17, color: COLORS.grey[600] }} />
            <Skeleton variant="rounded" width="58%" height={24} />
          </Stack>
          <Skeleton variant="rounded" width="94%" height={18} sx={{ mt: 0.8 }} />
          <Skeleton variant="rounded" width="74%" height={18} sx={{ mt: 0.6 }} />
        </Box>

        <Stack direction="row" spacing={0.2} alignItems="center">
          <Skeleton variant="rounded" width={24} height={24} />
          <Skeleton variant="circular" width={24} height={24} />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Box
            key={`metric-skeleton-${index}`}
            sx={{
              flex: 1,
              px: 1.1,
              py: 0.8,
              borderRadius: 1.25,
              border: `1px solid ${alpha(COLORS.grey[600], 0.45)}`,
              backgroundColor: alpha(COLORS.grey[800], 0.42),
            }}
          >
            <Skeleton variant="rounded" width="72%" height={13} />
            <Skeleton variant="rounded" width="58%" height={18} sx={{ mt: 0.6 }} />
          </Box>
        ))}
      </Stack>

      <Box
        sx={{
          mt: "auto",
          height: DASHBOARD_PREVIEW_HEIGHT,
          borderRadius: 1.5,
          overflow: "hidden",
          border: `1px solid ${alpha(COLORS.grey[600], 0.5)}`,
          background: alpha(COLORS.grey[900], 0.68),
          p: 1.2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Skeleton variant="rounded" width="38%" height={16} />
        <Skeleton variant="rounded" width="100%" height={44} />
        <Skeleton variant="rounded" width="84%" height={44} />
        <Skeleton variant="rounded" width="92%" height={44} />
      </Box>
    </Card>
  );
};

const DashboardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { show } = React.useContext(DialogContext);
  const { data: dashboards = [], isLoading } = useDashboards();

  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [rowMenu, setRowMenu] = React.useState<{
    anchorEl: HTMLElement;
    dashboard: Dashboard;
  } | null>(null);

  React.useEffect(() => {
    setSelectedIds((current) =>
      current.filter((id) => dashboards.some((dashboard) => dashboard.id === id)),
    );
  }, [dashboards]);

  const cardGridSx = {
    display: "grid",
    gap: 2,
    gridTemplateColumns: {
      xs: "1fr",
      md: "repeat(2, minmax(0, 1fr))",
      xl: "repeat(3, minmax(0, 1fr))",
    },
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Stack direction="row" spacing={1.1} alignItems="center">
            <DashboardRoundedIcon sx={{ color: COLORS.primary[300], fontSize: 20 }} />
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: COLORS.grey[100] }}>
              Control Planes
            </Typography>
          </Stack>
          <Typography sx={{ color: COLORS.grey[500], mt: 0.6 }}>
            Select a Control Plane to navigate its services.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon fontSize="small" />}
            onClick={() => {
              show({ dialog: "new-dashboard" });
            }}
          >
            New
          </Button>
          <DeleteButton
            disabled={selectedIds.length === 0}
            disabledReason="Select at least one Control Plane."
            onClick={() => {
              if (selectedIds.length === 0) {
                return;
              }
              show({
                dialog: "confirm-delete",
                entity: "dashboard",
                ids: selectedIds.join(","),
              });
            }}
          />
        </Stack>
      </Stack>

      {isLoading && dashboards.length === 0 ? (
        <Box sx={cardGridSx}>
          {Array.from({ length: DASHBOARD_SKELETON_COUNT }).map((_, index) => (
            <DashboardCardSkeleton key={`dashboard-card-skeleton-${index}`} />
          ))}
        </Box>
      ) : dashboards.length === 0 ? (
        <Card
          variant="glass"
          sx={{
            p: 3,
            borderRadius: 2.5,
            border: `1px solid ${alpha(COLORS.grey[600], 0.45)}`,
          }}
        >
          <Stack spacing={0.8}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.grey[100] }}>
              No Control Planes found
            </Typography>
            <Typography sx={{ color: COLORS.grey[500] }}>
              Create a Control Plane to start mapping and monitoring services.
            </Typography>
          </Stack>
        </Card>
      ) : (
        <Box sx={cardGridSx}>
          {dashboards.map((dashboard) => {
            const selected = selectedIds.includes(dashboard.id);

            return (
              <Card
                key={dashboard.id}
                variant="glass"
                onClick={() => {
                  navigate(`/dashboards/${dashboard.id}/services`);
                }}
                sx={{
                  p: 1.6,
                  borderRadius: 2.2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  cursor: "pointer",
                  border: `1px solid ${
                    selected
                      ? alpha(COLORS.primary[400], 0.55)
                      : alpha(COLORS.grey[700], 0.75)
                  }`,
                  background: selected
                    ? `linear-gradient(150deg, ${alpha(COLORS.primary[500], 0.18)}, ${alpha(
                        COLORS.grey[900],
                        0.9,
                      )})`
                    : undefined,
                  transition: "border-color 130ms ease, transform 130ms ease, box-shadow 130ms ease",
                  ":hover": {
                    borderColor: alpha(COLORS.primary[400], 0.55),
                    boxShadow: `0 0 0 1px ${alpha(COLORS.primary[400], 0.22)}`,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <DashboardRoundedIcon sx={{ fontSize: 17, color: COLORS.primary[300] }} />
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 750,
                          color: COLORS.grey[100],
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {dashboard.name}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        color: COLORS.grey[500],
                        mt: 0.45,
                        fontSize: 13,
                        lineHeight: 1.35,
                        minHeight: 36,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {dashboard.description || "No description"}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.2} alignItems="center">
                    <Checkbox
                      checked={selected}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onChange={(_, checked) => {
                        setSelectedIds((current) => {
                          if (checked) {
                            if (current.includes(dashboard.id)) {
                              return current;
                            }
                            return [...current, dashboard.id];
                          }
                          return current.filter((id) => id !== dashboard.id);
                        });
                      }}
                      sx={{
                        p: 0.5,
                        color: COLORS.grey[500],
                        "&.Mui-checked": { color: COLORS.primary[300] },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setRowMenu({
                          dashboard,
                          anchorEl: event.currentTarget,
                        });
                      }}
                    >
                      <MoreVertRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Box
                    sx={{
                      flex: 1,
                      px: 1.1,
                      py: 1,
                      minHeight: 90,
                      borderRadius: 1.25,
                      border: `1px solid ${alpha(COLORS.grey[600], 0.45)}`,
                      backgroundColor: alpha(COLORS.grey[800], 0.42),
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ color: COLORS.grey[500], fontSize: 12, mb: 0.4 }}>
                      Platform health
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                      <HealthScore score={dashboard.metrics.platformHealth} size="md" />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      px: 1.1,
                      py: 1,
                      minHeight: 90,
                      borderRadius: 1.25,
                      border: `1px solid ${alpha(COLORS.grey[600], 0.45)}`,
                      backgroundColor: alpha(COLORS.grey[800], 0.42),
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ color: COLORS.grey[500], fontSize: 12, mb: 0.4 }}>
                      Active events
                    </Typography>
                    <Stack direction="row" spacing={0.65} alignItems="center" justifyContent="center">
                      <WarningAmberRoundedIcon
                        sx={{ fontSize: 17, color: COLORS.primary[300] }}
                      />
                      <Typography sx={{ color: COLORS.grey[100], fontWeight: 800, fontSize: 17 }}>
                        {dashboard.metrics.activeEvents}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      px: 1.1,
                      py: 1,
                      minHeight: 90,
                      borderRadius: 1.25,
                      border: `1px solid ${alpha(COLORS.grey[600], 0.45)}`,
                      backgroundColor: alpha(COLORS.grey[800], 0.42),
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ color: COLORS.grey[500], fontSize: 12, mb: 0.4 }}>
                      Risk trending
                    </Typography>
                    <Stack direction="row" spacing={0.65} alignItems="center" justifyContent="center">
                      <TrendingUpRoundedIcon
                        sx={{ fontSize: 17, color: COLORS.primary[300] }}
                      />
                      <Typography sx={{ color: COLORS.grey[100], fontWeight: 800, fontSize: 17 }}>
                        {dashboard.metrics.riskTrending}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    mt: "auto",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    border: `1px solid ${alpha(COLORS.grey[600], 0.5)}`,
                    background: alpha(COLORS.grey[900], 0.68),
                    pointerEvents: "none",
                  }}
                >
                  <DashboardDiagramPreview serviceMapId={dashboard.id} />
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      <RowActionsMenu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        onEdit={() => {
          const dashboardId = rowMenu?.dashboard.id;
          if (!dashboardId) {
            return;
          }
          show({
            dialog: "edit-dashboard",
            dashboardId,
          });
        }}
      />
    </Box>
  );
};

export default DashboardsPage;
